import React, { useState, useMemo } from "react";
import { ScrollView, View, TouchableOpacity, StyleSheet, TextInput } from "react-native";
import NSText from "./NSText";
import { AntDesign } from "@expo/vector-icons";
import { Feather } from "@expo/vector-icons";

interface NestedDataType {
  id: string;
  name: string;
  children:
    | {
        id: string;
        name: string;
        children: { id: string; name: string }[] | null;
      }[]
    | null;
}

const shadowStyle = {
  marginBottom: 2,
  shadowColor: "#000",
  shadowOffset: { width: 0, height: 2 },
  shadowOpacity: 0.25,
  shadowRadius: 3.84,
  elevation: 5,
};

const GlobalMultiSelectNestedPicker = ({
  width,
  coords,
  data,
  selectedIds,
  onItemPress,
  hasSearch = false,
  searchPlaceholder
}: {
  width: number;
  coords: { x: number; y: number };
  data: NestedDataType[];
  selectedIds?: string[];
  onItemPress: (value: any, index: number) => void;
  hasSearch?: boolean;
  searchPlaceholder?: string;
}) => {
  const [openedIndex, setOpenedIndex] = useState<number>(-1);
  const [openedSubIndex, setOpenedSubIndex] = useState<number>(-1);
  const [searchText, setSearchText] = useState<string>("");

  // Search filter function for nested data
  const filterNestedData = (data: NestedDataType[], searchTerm: string): NestedDataType[] => {
    if (!searchTerm.trim()) return data;

    return data.filter(item => {
      const matchesName = item.name.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (matchesName) return true;
      
      if (item.children) {
        const filteredChildren = item.children.filter(child => {
          const childMatchesName = child.name.toLowerCase().includes(searchTerm.toLowerCase());
          
          if (childMatchesName) return true;
          
          if (child.children) {
            return child.children.some(grandChild => 
              grandChild.name.toLowerCase().includes(searchTerm.toLowerCase())
            );
          }
          
          return false;
        });
        
        if (filteredChildren.length > 0) {
          return {
            ...item,
            children: filteredChildren.map(child => {
              if (child.children) {
                const filteredGrandChildren = child.children.filter(grandChild =>
                  grandChild.name.toLowerCase().includes(searchTerm.toLowerCase())
                );
                return {
                  ...child,
                  children: filteredGrandChildren.length > 0 ? filteredGrandChildren : child.children
                };
              }
              return child;
            })
          };
        }
      }
      
      return false;
    });
  };

  const filteredData = useMemo(() => {
    return filterNestedData(data, searchText);
  }, [data, searchText]);

  return (
    <View style={[styles?.container, { width, top: coords.y, left: coords.x, zIndex: 1400 }]}>
      {hasSearch && (
        <View style={styles.searchContainer}>
          <TextInput
            style={styles.searchInput}
            placeholder={searchPlaceholder || "Search..."}
            value={searchText}
            onChangeText={setSearchText}
            placeholderTextColor="#7D7D7D"
          />
          <Feather name="search" size={16} color="#7D7D7D" style={styles.searchIcon} />
        </View>
      )}
      <ScrollView showsVerticalScrollIndicator={false}>
        {filteredData?.map((item, index) => {
          return !!item?.children && item?.children?.length > 0 ? (
            <View style={styles?.nested1stContainer} key={`${item?.id}-${index}`}>
              <TouchableOpacity
                  style={[
                    styles?.nested1stTO,
                    {
                      borderTopStartRadius: index === 0 && !hasSearch ? 5 : 0,
                      borderTopEndRadius: index === 0 && !hasSearch ? 5 : 0,
                      borderBottomStartRadius: index === filteredData?.length - 1 ? 5 : 0,
                      borderBottomEndRadius: index === filteredData?.length - 1 ? 5 : 0,
                      backgroundColor: selectedIds?.includes(item?.id)
                        ? "#6300A35D"
                        : "",
                    },
                  ]}
                onPress={() => {
                  if (openedIndex === index) {
                    setOpenedIndex(-1);
                    return;
                  }
                  setOpenedIndex(index);
                }}
              >
                <View style={styles?.nested1stView}>
                  <TouchableOpacity
                    style={[
                      styles?.itemView,
                      {
                        borderColor: selectedIds?.includes(item?.id)
                          ? "#6300A3"
                          : "#7D7D7D",
                        backgroundColor: selectedIds?.includes(item?.id)
                          ? "#6300A3"
                          : "",
                      },
                    ]}
                    onPress={() => {
                      onItemPress(item, index);
                    }}
                  >
                    {selectedIds?.includes(item?.id) ? (
                      <Feather name="check" size={12} color="white" />
                    ) : null}
                  </TouchableOpacity>
                  <NSText
                    style={{
                      maxWidth: "90%",
                      flexShrink: 1,
                      marginHorizontal: 5,
                      fontSize: 12,
                      color: selectedIds?.includes(item?.id)
                        ? "#303030"
                        : "#7D7D7D",
                    }}
                    text={item?.name}
                  />
                </View>
                {item?.children?.length > 0 ? (
                  <AntDesign
                    name={openedIndex === index ? "caret-up" : "caret-down"}
                    size={10}
                    color="#303030"
                  />
                ) : null}
              </TouchableOpacity>
              {openedIndex === index ? (
                <ScrollView showsVerticalScrollIndicator={false}>
                  {item?.children?.map((val, idx) => {
                    return !!val?.children ? (
                      <View key={`${val?.id}-${idx}`}>
                        <TouchableOpacity
                          style={styles?.nested2ndTO}
                          onPress={() => {
                            if (openedSubIndex === idx) {
                              setOpenedSubIndex(-1);
                              return;
                            }
                            setOpenedSubIndex(idx);
                          }}
                        >
                          <NSText
                            style={styles?.nested2ndText}
                            text={val?.name}
                          />
                          <AntDesign
                            name={
                              openedSubIndex === idx ? "caret-up" : "caret-down"
                            }
                            size={10}
                            color="#303030"
                          />
                        </TouchableOpacity>
                        {openedSubIndex === idx ? (
                          <ScrollView showsVerticalScrollIndicator={false}>
                            {val?.children?.map((v, i) => {
                              return (
                                <TouchableOpacity
                                  key={`opened-${v?.id}-${i}`}
                                  style={[
                                    styles?.openedTO,
                                    {
                                      backgroundColor: selectedIds?.includes?.(
                                        v?.id
                                      )
                                        ? "#6300A35D"
                                        : "",
                                    },
                                  ]}
                                  onPress={() => {
                                    console.log("V", v);
                                    onItemPress(v, i);
                                  }}
                                >
                                  <View
                                    style={[
                                      styles?.itemView,
                                      {
                                        borderColor: selectedIds?.includes(
                                          v?.id
                                        )
                                          ? "#6300A3"
                                          : "#7D7D7D",
                                        backgroundColor: selectedIds?.includes(
                                          v?.id
                                        )
                                          ? "#6300A3"
                                          : "",
                                      },
                                    ]}
                                  >
                                    {selectedIds?.includes(v?.id) ? (
                                      <Feather
                                        name="check"
                                        size={12}
                                        color="white"
                                      />
                                    ) : null}
                                  </View>
                                  <NSText
                                    style={{
                                      maxWidth: "90%",
                                      flexShrink: 1,
                                      marginStart: 5,
                                      fontSize: 12,
                                      color: selectedIds?.includes?.(v?.id)
                                        ? "#303030"
                                        : "#7D7D7D",
                                    }}
                                    text={v?.name}
                                  />
                                </TouchableOpacity>
                              );
                            })}
                          </ScrollView>
                        ) : null}
                      </View>
                    ) : (
                      <TouchableOpacity
                        key={`${val?.id}-${idx}`}
                        style={[
                          styles?.openedTO2,
                          {
                            backgroundColor: selectedIds?.includes?.(val?.id)
                              ? "#6300A35D"
                              : "",
                          },
                        ]}
                        onPress={() => {
                          console.log("Val", val);
                          onItemPress(val, idx);
                        }}
                      >
                        <View
                          style={[
                            styles?.itemView,
                            {
                              borderColor: selectedIds?.includes(val?.id)
                                ? "#6300A3"
                                : "#7D7D7D",
                              backgroundColor: selectedIds?.includes(val?.id)
                                ? "#6300A3"
                                : "",
                            },
                          ]}
                        >
                          {selectedIds?.includes(val?.id) ? (
                            <Feather name="check" size={12} color="white" />
                          ) : null}
                        </View>
                        <NSText
                          style={{
                            maxWidth: "90%",
                            flexShrink: 1,
                            marginStart: 5,
                            fontSize: 12,
                            color: selectedIds?.includes?.(val?.id)
                              ? "#303030"
                              : "#7D7D7D",
                          }}
                          text={val?.name}
                        />
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              ) : null}
            </View>
          ) : (
            <TouchableOpacity
              key={`outside-${item?.id}-${index}`}
              style={[
                styles?.outsideTO,
                {
                  backgroundColor: selectedIds?.includes?.(item?.id)
                    ? "#6300A35D"
                    : "",
                  borderTopStartRadius: index === 0 && !hasSearch ? 5 : 0,
                  borderTopEndRadius: index === 0 && !hasSearch ? 5 : 0,
                  borderBottomStartRadius: index === filteredData?.length - 1 ? 5 : 0,
                  borderBottomEndRadius: index === filteredData?.length - 1 ? 5 : 0,
                },
              ]}
              onPress={() => {
                onItemPress(item, index);
              }}
            >
              <View
                style={[
                  styles?.itemView,
                  {
                    borderColor: selectedIds?.includes(item?.id)
                      ? "#6300A3"
                      : "#7D7D7D",
                    backgroundColor: selectedIds?.includes(item?.id)
                      ? "#6300A3"
                      : "",
                  },
                ]}
              >
                {selectedIds?.includes(item?.id) ? (
                  <Feather name="check" size={12} color="white" />
                ) : null}
              </View>
              <NSText
                style={{
                  maxWidth: "90%",
                  flexShrink: 1,
                  marginStart: 5,
                  fontSize: 12,
                  color: selectedIds?.includes?.(item?.id)
                    ? "#303030"
                    : "#7D7D7D",
                }}
                text={item?.name}
              />
            </TouchableOpacity>
          );
        })}
      </ScrollView>
    </View>
  );
};

export default GlobalMultiSelectNestedPicker;

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    maxHeight: 300,
    borderRadius: 5,
    backgroundColor: "#F7F2FA",
    ...shadowStyle,
  },
  nested1stContainer: { width: "100%" },
  nested1stTO: {
    flexDirection: "row",
    padding: 10,
    backgroundColor: "",
    alignItems: "center",
    justifyContent: "space-between",
    elevation: 5,
  },
  nested1stText: {
    maxWidth: "90%",
    flexShrink: 1,
    marginEnd: 5,
    fontSize: 12,
    color: "#303030",
  },
  nested2ndTO: {
    flexDirection: "row",
    marginStart: 10,
    padding: 10,
    backgroundColor: "",
    alignItems: "center",
    justifyContent: "space-between",
    elevation: 5,
  },
  nested2ndText: {
    maxWidth: "90%",
    flexShrink: 1,
    marginEnd: 5,
    fontSize: 12,
    color: "#303030",
  },
  openedTO: {
    flexDirection: "row",
    padding: 10,
    paddingStart: 30,
    alignItems: "center",
    elevation: 5,
  },
  openedText: { maxWidth: "90%", flexShrink: 1, fontSize: 12 },
  openedTO2: {
    flexDirection: "row",
    padding: 10,
    paddingStart: 20,
    alignItems: "center",
    elevation: 5,
  },
  openedText2: { maxWidth: "90%", flexShrink: 1, fontSize: 12 },
  outsideTO: {
    flexDirection: "row",
    padding: 10,
    alignItems: "center",
    elevation: 5,
  },
  outsideText: { maxWidth: "90%", flexShrink: 1, fontSize: 12 },
  itemView: {
    width: 16,
    height: 16,
    borderWidth: 2,
    borderRadius: 2,
    alignItems: "center",
    justifyContent: "center",
  },
  nested1stView: { flexDirection: "row", alignItems: "center" },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    padding: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#E0E0E0",
    backgroundColor: "#F7F2FA",
    borderTopStartRadius: 5,
    borderTopEndRadius: 5,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    color: "#303030",
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: "white",
    borderRadius: 4,
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  searchIcon: {
    marginLeft: 8,
  },
});
